<template>
  <div class="score-modal" role="alert">
    <div
      class="score-modal__background"
      @click.stop="$emit('close-score-modal')"
    >
      <canvas id="confetti" class="score-modal__confetti"></canvas>
    </div>
    <div class="score-modal__wrapper-scroller">
      <div class="score-modal__wrapper">
        <button class="score-modal__close" @click="$emit('close-score-modal')">
          <span class="score-modal__close--icon material-icons">clear</span>
        </button>
        <div
          :class="[
            'score-modal__body',
            globalHasPassed && 'score-modal__body--attempt-success',
          ]"
        >
          <div
            :class="[
              'score-modal__scoring',
              globalHasPassed
                ? 'score-modal__scoring--success'
                : 'score-modal__scoring--fail',
            ]"
          >
            <img class="score-modal__img" :src="statusImage" />
            <div
              v-if="globalHasPassed"
              class="score-modal__details score-modal__passed"
            >
              <div class="score-modal__message">Congratulations!</div>
              <div class="score-modal__result-label">You passed</div>
              <div class="score-modal__cta">
                <a
                  v-if="!certAccredibleUrl"
                  class="btn btn--primary score-modal__btn"
                  @click="handleRouterLinkClick"
                >
                  Go to your certificates
                </a>
                <a
                  v-else
                  :href="certAccredibleUrl"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="btn btn--primary score-modal__btn"
                >
                  Go to certificate
                </a>
              </div>
            </div>
            <div v-else class="score-modal__details score-modal__details--fail">
              <div class="score-modal__message">We're sorry.</div>
              <div class="score-modal__result-label">You didn't pass</div>
              <div class="score-modal__message-info">
                {{ sorryMessage }}
              </div>
            </div>
          </div>
          <div class="score-modal__grading">
            <div class="score-modal__grading-wrapper flat-scroll-current">
              <div class="score-modal__grading-header-wrapper">
                <h3 class="score-modal__grading-title">Exam Results</h3>
              </div>
              <div class="score-modal__details-wrapper">
                <div class="'score-modal-domains'">
                  <div class="score-modal-domains__header">
                    <div class="score-modal__grading-header-score-wrapper">
                      <div class="score-modal-domains__subtitle-type">
                        Domains
                      </div>
                      <div class="score-modal__grading-score-wrapper">
                        <div class="score-modal__grading-score">
                          <span class="score-modal__grading-score-label"
                            >Overall required score:</span
                          >
                          <span class="score-modal__grading-score-result"
                            >{{
                              roundTo2(attempt.min_global_score_to_pass)
                            }}%</span
                          >
                        </div>
                        <div class="score-modal__grading-score">
                          <span
                            :class="[
                              'score-modal__grading-score-label',
                              attempt.has_passed ? 'passed' : 'failed',
                            ]"
                            >Your score:</span
                          >
                          <span
                            :class="[
                              'score-modal__grading-score-result',
                              attempt.has_passed ? 'passed' : 'failed',
                            ]"
                            >{{ roundTo2(attempt.score) }}%</span
                          >
                        </div>
                      </div>
                    </div>
                    <span
                      :class="[
                        'score-modal-objectives__domain-status pill',
                        attempt.has_passed ? 'pill--completed' : 'pill--failed',
                      ]"
                      >{{ examStatusLabel }}</span
                    >
                  </div>
                  <ExamScoreModalObjectives
                    :domains="attempt.score_per_domain"
                  />
                </div>
                <div
                  v-if="hasLabattempt"
                  id="score-modal-lab"
                  class="score-modal-lab"
                >
                  <div class="score-modal-domains__header">
                    <div class="score-modal__grading-header-score-wrapper">
                      <div class="score-modal-domains__subtitle-type">Lab</div>
                      <div class="score-modal__grading-score-wrapper"></div>
                    </div>
                    <span
                      :class="[
                        'score-modal-objectives__domain-status pill',
                        attempt.user_lab_attempt.has_passed
                          ? 'pill--completed'
                          : 'pill--failed',
                      ]"
                      >{{ labStatusLabel }}</span
                    >
                  </div>

                  <LabScoreModalObjectives
                    :labAttempt="attempt.user_lab_attempt"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
        <div v-if="globalHasPassed" class="score-modal__footer">
          <a
            v-if="!certAccredibleUrl"
            class="btn btn--primary score-modal__btn"
            @click="handleRouterLinkClick"
          >
            Go to your certificates
          </a>
          <a
            v-else
            :href="certAccredibleUrl"
            target="_blank"
            rel="noopener noreferrer"
            class="btn btn--primary score-modal__btn"
          >
            Go to certificate
          </a>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Confetti } from 'vue-confetti'
import ExamScoreModalObjectives from '@/components/modals/ExamScoreModalObjectives.vue'
import LabScoreModalObjectives from '@/components/modals/LabScoreModalObjectives.vue'

export default {
  name: 'ExamScoreModal',
  components: {
    ExamScoreModalObjectives,
    LabScoreModalObjectives,
  },
  props: {
    attempt: {
      type: Object,
      required: true,
    },
    cert: {
      type: Object,
      required: false,
      default: () => {
        return {}
      },
    },
  },
  data: () => {
    return {
      confetti: null,
      options: {
        particles: [
          {
            type: 'circle',
          },
          {
            type: 'rect',
          },
        ],
        customCanvas: false,
        particlesPerFrame: 2,
        windSpeedMax: 1,
        canvasId: 'confetti',
        defaultColors: ['#A793F6', '#5B4B9B', '#ffffff', '#EE3B00', '#EF7D22'],
      },
    }
  },
  computed: {
    attemptDomains() {
      return this.attempt.score_per_domain
    },
    statusImage() {
      return this.globalHasPassed
        ? require('@/assets/images/certification/success.png')
        : require('@/assets/images/certification/fail.png')
    },
    globalHasPassed() {
      return this.hasLabattempt
        ? this.attempt.has_passed && this.attempt.user_lab_attempt.has_passed
        : this.attempt.has_passed
    },
    hasLab() {
      return !!this.attempt.practice_lab
    },
    hasLabattempt() {
      return !!this.attempt.user_lab_attempt
    },
    sorryMessage() {
      return 'You must meet the minimum score requirements to pass this exam.'
    },
    questionsPassed() {
      return Boolean(this.attempt.has_passed)
    },
    questionsPassedText() {
      return this.questionsPassed
        ? 'Minimum per objective reached'
        : 'Minimum per objective not reached'
    },
    labPassed() {
      return !!this.attempt.user_lab_attempt?.has_passed
    },
    labPassedText() {
      return this.labPassed
        ? 'Minimum score reached'
        : 'Minimum score not reached'
    },
    labObjectives() {
      return this.attempt.user_lab_attempt?.lab_objectives
    },
    labObjectivesResult() {
      let totalLab = 0
      this.attempt.user_lab_attempt?.lab_objectives.map(o => {
        if (o.has_passed) totalLab += parseInt(o.points)
      })
      return totalLab
    },
    labObjectivesTotal() {
      let totalLab = 0
      this.attempt.user_lab_attempt?.lab_objectives.map(o => {
        totalLab += parseInt(o.points)
      })
      return totalLab
    },
    examStatusLabel() {
      return this.attempt.has_passed ? 'Passed' : 'Failed'
    },
    labStatusLabel() {
      return this.attempt.user_lab_attempt.has_passed ? 'Passed' : 'Failed'
    },
    certAccredibleUrl() {
      return this.cert?.accredible_credential_url
    },
  },
  mounted() {
    if (this.globalHasPassed) {
      this.$nextTick(() => {
        const canvas = document.getElementById('confetti')
        canvas.width = document.body.clientWidth // document.width is obsolete
        canvas.height = document.body.clientHeight // document.height is obsolete
        this.confetti = new Confetti()
        this.confetti.start(this.options)
        setTimeout(() => {
          this.confetti?.stop()
        }, 7000)
      })
    }
  },
  created() {
    const body = document.querySelector('body')
    body.classList.add('score-modal-open')
  },
  destroyed() {
    const body = document.querySelector('body')
    body.classList.remove('score-modal-open')
    if (this.confetti) {
      this.confetti.stop()
      this.confetti = null
    }
    window.removeEventListener('resize', () => null)
  },
  methods: {
    domainStatusLabel(domain) {
      return domain.domain_passed ? 'Passed' : 'Failed'
    },
    roundTo2(n) {
      return Math.floor(n * 100)
    },
    closeModal() {
      this.$emit('close-score-modal')
    },
    cleanKeyName(name) {
      return name.replace(/^([0-9]+. )/, '')
    },
    handleRouterLinkClick() {
      const currentRoute = this.$route
      if (
        currentRoute.name === 'user-dashboard' &&
        currentRoute?.params?.mode === 'certifications'
      ) {
        this.closeModal()
      } else {
        this.$router.push({
          name: 'user-dashboard',
          params: { mode: 'certifications' },
        })
      }
    },
  },
}
</script>
<style lang="scss" scoped>
.score-modal {
  $this: &;
  position: fixed;
  top: 0;
  left: 0;
  z-index: 99999;
  width: 100%;
  height: 100%;
  transition: opacity 0.3s ease;
  display: flex;
  align-items: center;

  @include mobile {
    padding: 15px;
  }

  @include desktop {
    padding: 5vh;
  }
  &__wrapper-scroller {
    overflow: auto;
    width: 100%;
    height: 100%;
    @include desktop {
      display: flex;
      align-items: center;
    }
  }
  &__wrapper {
    display: flex;
    flex-wrap: wrap;
    position: relative;
    margin: 0px auto;
    overflow: hidden;
    color: var(--white);
    max-width: 1692px;
    font-size: rem(16px);
    width: 100%;
    justify-content: space-evenly;
    align-content: center;
    background-color: var(--darker-gray);
    @include mobile {
      padding: 0;
      border-radius: 35px;
      padding: 0;
    }
    @include desktop {
      max-height: 1200px;
      width: 1260px;
    }
  }
  &__background {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: var(--black);
    backdrop-filter: saturate(180%) blur(20px);
  }
  &__close {
    width: 50px;
    height: 50px;
    position: absolute;
    right: 5px;
    top: 10px;
    background: none;
    border: none;
    cursor: pointer;
    color: white;
    @include tablet {
      right: 20px;
    }
    &--icon {
      fill: var(--white);
    }
  }
  &__body {
    display: flex;
    flex-wrap: wrap;
    justify-content: space-around;
    max-width: 100vw !important;
    background-color: var(--darker-gray);
    @include mobile {
      align-items: stretch;
      background-color: none;
      margin-bottom: 0;
    }
    @include tablet {
      width: 1260px;
      max-height: 90vh !important;
    }
    &--attempt-success {
      margin-bottom: 60px;
      @include mobile {
        margin-bottom: 0;
      }
    }
  }
  &__details {
    text-align: center;
    margin-bottom: 15px;
    @include mobile {
      margin-bottom: 10px;
    }
    @include tablet {
      margin-top: -45px;
    }
    &-wrapper {
      background: var(--glass-mid-gray);
      padding: 20px;
      border-radius: 10px;
      @include mobile {
        padding: 30px;
      }
      @include tablet {
        background: none;
        padding: 0;
        border-radius: 0;
      }
    }
    &-link {
      display: flex;
      justify-content: flex-end;

      @include mobile {
        display: none;
      }
    }
  }
  &__result-label {
    font-size: rem(18px);
    line-height: 24px;
    text-align: center;
    margin-bottom: 5px;
    @include mobile {
      font-size: rem(24px);
      line-height: 32px;
      margin-bottom: 30px;
    }
    @include tablet {
      font-size: rem(24px);
      line-height: 32px;
      margin-bottom: 5px;
    }
  }
  &__message {
    font-family: var(--font-bold);
    font-weight: bold;
    font-size: rem(32px);
    line-height: 38px;
    color: var(--carnation);
    margin: 0 auto 5px auto;
    @include mobile {
      font-size: rem(40px);
      line-height: 52px;
    }

    &-info {
      font-size: rem(12px);
      line-height: 14px;
      color: var(--light-blue-gray);
      position: relative;
      @include mobile {
        margin-bottom: 30px;
        font-size: rem(14px);
        line-height: 20px;
      }

      @include tablet {
        width: 93%;
        margin-bottom: 20px;
      }
    }
  }
  &__passed &__message {
    color: var(--positive-light);
  }
  &__btn {
    @include mobile {
      margin: 0 0 30px;
    }
    @include tablet {
      margin: 25px 0 0;
    }
  }
  &__scoring {
    display: grid;
    justify-items: center;
    align-content: center;
    width: 100%;
    padding: 67px 30px 0;
    background-color: var(--darker-gray);
    @include mobile {
      padding: 54px 30px 0;
    }
    @include tablet {
      padding: 70px 30px;
      max-width: 370px;
    }
    @include desktop {
      padding: 80px;
      max-width: 500px;
    }
    img {
      width: 100%;
      max-width: 280px;
      margin-top: -50px;
      margin-left: 20px;
    }
    &--success {
      background-image: url('../../assets/images/certification/success-mob.png');
      background-repeat: no-repeat;
      background-size: contain;
      background-position-y: center;
      padding-bottom: 10px;
      @include mobile {
        background-image: none;
      }
    }

    &--fail {
      background-image: url('../../assets/images/certification/fail-mob.png');
      background-repeat: no-repeat;
      background-size: contain;
      background-position-y: center;
      @include mobile {
        background-image: none;
      }
    }
  }
  &__grading {
    background: none;
    padding: 0;
    min-width: 345px;
    width: 100%;
    overflow: hidden;
    border-radius: 0;
    @include mobile {
      flex: 1;
      border-radius: 35px;
    }
    @include tablet {
      background: var(--glass-mid-gray);
      max-height: 90vh;
    }
    &-header {
      display: flex;
      flex-direction: column;
      margin-bottom: 20px;
      padding: 0 20px;
      @include mobile {
        flex-direction: row;
        margin-bottom: 10px;
        padding: 0 30px;
      }
      @include tablet {
        margin-bottom: 30px;
        padding: 0;
      }
    }
    &-header-wrapper {
      display: none;
      @include tablet {
        display: flex;
        margin-bottom: 30px;
      }
    }
    &-score-wrapper {
      @include mobile {
        display: flex;
        margin-left: auto;
      }
    }
    &-score {
      display: flex;
      align-items: center;
      &:first-child {
        margin-left: auto;
        margin-right: 10px;
        padding-right: 10px;
        @include mobile {
          border-right: 1px solid var(--tundora-gray);
        }
      }
      &-label {
        font-size: rem(16);
        line-height: rem(24);
      }
      &-result {
        margin-left: 5px;
        font-size: rem(18);
        line-height: rem(24);
      }
    }
    &-header-score-wrapper {
      display: flex;
      flex-direction: column;
      align-content: center;
      width: 100%;
      @include mobile {
        flex-direction: row;
      }
    }
    &-title {
      font-size: rem(20px);
      line-height: 26px;
      font-family: var(--font-bold);
      margin: 0;
      display: flex;
      align-items: center;
      justify-content: space-between;

      @include tablet {
        font-size: rem(32px);
        font-family: var(--font-regular);
        line-height: 100%;
        justify-content: flex-start;
      }
    }
  }
  &__grading-wrapper {
    height: 100%;
    @include tablet {
      padding: 74px 50px 30px;
    }
  }
  &-domains,
  &-lab {
    @include tablet {
      margin-bottom: rem(30px);
    }
    &__group {
      display: flex;
      flex-direction: column;
      justify-content: space-between;

      @include mobile {
        flex-direction: row;
        align-items: center;
      }
    }
    &__subtitle {
      font-size: rem(18px);
      line-height: 24px;

      @include mobile {
        font-size: rem(18px);
        line-height: 24px;
      }
      &-type {
        margin: 10px 0;
        font-size: rem(20);
        font-family: var(--font-bold);
        &:first-child() {
          margin: 0 0 10px;
        }
        @include tablet {
          margin: 30px 0 10px;
          line-height: 26px;
        }
      }
      &--passed {
        color: var(--positive-light);
      }
      &--failed {
        color: var(--carnation);
      }
    }
  }
  &-lab {
    &:last-child {
      margin: 20px 0 0 0;
    }
  }
  &-domains {
    &__header {
      display: flex;
      width: 100%;
      margin-bottom: 10px;
    }
    &__subtitle-type {
      margin-top: 0;
      margin-bottom: 0;
    }
  }
  &__confetti {
    width: 100%;
  }
  &__footer {
    display: flex;
    width: 100%;
    justify-content: center;
    background: var(--black-footer);
    position: fixed;
    bottom: 0;
    padding: 14.5px 20px;
    a {
      width: 100%;
    }

    @include mobile {
      display: none;
    }
  }
  &__img {
    display: none;
    @include tablet {
      display: inherit;
    }
  }
  &__cta {
    display: none;
    @include mobile {
      display: inherit;
    }
  }
}
.passed {
  color: var(--positive-light);
}
.failed {
  color: var(--carnation);
}
</style>
